import Big from "big.js";

const ItemType = {
  topic: "topic",
  attribute: "attribute",
  indicator: "indicator",
  condition: "condition"
};

class EvaluateFormItem {
  constructor(json) {
    const {
      id,
      name,
      title,
      children,
      order,
      studyClass,
      type,
      score,
      record,
      isMastered,
      indicator,
      desirableAttribute,
      desirableCondition,
      developmentTopic,
      parent
    } = json;
    this.id = id;
    this.name = name;
    this.title = title;
    this.children = children;
    this.order = order;
    this.studyClass = studyClass;
    this.type = type;
    this.score = score;
    this.record = record;
    this.isMastered = isMastered;
    this.indicator = indicator;
    this.desirableAttribute = desirableAttribute;
    this.desirableCondition = desirableCondition;
    this.developmentTopic = developmentTopic;
    this.parent = parent;
  }

  hasChild() {
    if (this.children) {
      return true;
    }
    return false;
  }

  childrenCount() {
    if (this.children) {
      let childrenCount = this.children.length;
      return childrenCount;
    }

    return 0;
  }

  scoredChildrenCount(term, time) {
    if (this.children) {
      let childrenCount = this.children.filter(
        item => item.computedScore(term, time) > 0
      );
      return childrenCount.length;
    }

    return 0;
  }

  childrenCompare(term, time) {
    return `${this.scoredChildrenCount(term, time)} / ${this.childrenCount()}`;
  }

  allConditionCount() {
    let count = 0;
    if (this.type === ItemType.topic) {
      this.children.forEach(attribute => {
        attribute.children.forEach(indicator => {
          count += indicator.childrenCount();
        });
      });
      return count;
    } else if (this.type === ItemType.attribute) {
      this.children.forEach(indicator => {
        count += indicator.childrenCount();
      });

      return count;
    } else if (this.type === ItemType.indicator) {
      return this.children.length;
    }
    return 1;
  }

  classroomProgress(students, recordedCount) {
    let progress = 0;
    const expectNumber = this.allConditionCount() * students;
    recordedCount.forEach(group => {
      let {
        topic: targetTopic,
        attribute: targetAttribute,
        indicator: targetIndicator,
        condition: targetCondition
      } = group._id;

      if (this.type === ItemType.topic) {
        if (this.id === targetTopic) {
          progress += group.count;
        }
      } else if (this.type === ItemType.attribute) {
        if (this.id === targetAttribute) {
          progress += group.count;
        }
      } else if (this.type === ItemType.indicator) {
        if (this.id === targetIndicator) {
          progress += group.count;
        }
      } else if (this.type === ItemType.condition) {
        if (this.id === targetCondition) {
          progress += group.count;
        }
      }
    });

    let percent = Math.round((progress / expectNumber) * 100);
    return `${percent}%`;
  }

  classroomProgressValue(students, recordedCount) {
    let progress = 0;
    const expectNumber = this.allConditionCount() * students;
    recordedCount.forEach(group => {
      let {
        topic: targetTopic,
        attribute: targetAttribute,
        indicator: targetIndicator,
        condition: targetCondition
      } = group._id;

      if (this.type === ItemType.topic) {
        if (this.id === targetTopic) {
          progress += group.count;
        }
      } else if (this.type === ItemType.attribute) {
        if (this.id === targetAttribute) {
          progress += group.count;
        }
      } else if (this.type === ItemType.indicator) {
        if (this.id === targetIndicator) {
          progress += group.count;
        }
      } else if (this.type === ItemType.condition) {
        if (this.id === targetCondition) {
          progress += group.count;
        }
      }
    });

    let percent = Math.round((progress / expectNumber) * 100);

    return percent;
  }

  allScoredConditionCount(term, time) {
    if (this.type === ItemType.topic) {
      let count = 0;
      this.children.forEach(attribute => {
        attribute.children.forEach(indicator => {
          indicator.children.forEach(condition => {
            if (condition.computedScore(term, time) > 0) {
              count += 1;
            }
          });
        });
      });
      return count;
    }
    return 0;
  }

  allChildrenCompared(term, time) {
    return `${this.allScoredConditionCount(
      term,
      time
    )}/${this.allConditionCount()}`;
  }

  computedScore(term, time) {
    if (this.hasChild()) {
      let childrenCount = this.children.length;

      if (this.type === ItemType.indicator) {
        const reducer = (accumulator, formItem) =>
          accumulator + formItem.score[`term${term}`][[`time${time}`]];
        let totalScore = this.children.reduce(reducer, 0);
        return totalScore / childrenCount;
      }

      const reducer = (accumulator, formItem) =>
        accumulator + formItem.computedScore(term, time);
      let totalScore = this.children.reduce(reducer, 0);
      return totalScore / childrenCount;
    }

    return this.score[`term${term}`][`time${time}`];
  }

  getProgress(term) {
    function toFixed(num, fixed) {
      var re = new RegExp("^-?\\d+(?:.\\d{0," + (fixed || -1) + "})?");
      return num.toString().match(re)[0];
    }

    function reducer(accumulator, formItem) {
      return accumulator + formItem.getProgress(term);
    }

    switch (this.type) {
      case ItemType.topic:
        var attributeScore = new Big(this.children.reduce(reducer, 0));
        var attributeAvg = attributeScore.div(this.childrenCount());
        return Number(attributeAvg.round(0));
      case ItemType.attribute:
        var indicatorsScore = new Big(this.children.reduce(reducer, 0));
        var indicatorAvg = indicatorsScore.div(this.childrenCount());
        return Number(indicatorAvg.round(0));
      case ItemType.indicator:
        var conditionScore = new Big(this.children.reduce(reducer, 0));
        var conditionAvg = conditionScore.div(this.childrenCount());
        return Number(toFixed(conditionAvg, 1));
      case ItemType.condition:
        var termScore = this.score[`term${term}`];
        var time1 = termScore.time1 ? 1 : 0;
        var time2 = termScore.time2 ? 1 : 0;
        return ((time1 + time2) / 2) * 100;
    }
  }

  getCalculatedScore(term) {
    function toFixed(num, fixed) {
      var re = new RegExp("^-?\\d+(?:.\\d{0," + (fixed || -1) + "})?");
      return num.toString().match(re)[0];
    }

    function reducer(accumulator, formItem) {
      return accumulator + formItem.getCalculatedScore(term);
    }

    switch (this.type) {
      case ItemType.topic:
        var attributeScore = new Big(this.children.reduce(reducer, 0));
        var attributeAvg = attributeScore.div(this.childrenCount());
        return Number(attributeAvg.round(0));
      case ItemType.attribute:
        var indicatorsScore = new Big(this.children.reduce(reducer, 0));
        var indicatorAvg = indicatorsScore.div(this.childrenCount());
        return Number(indicatorAvg.round(0));
      case ItemType.indicator:
        var conditionScore = new Big(this.children.reduce(reducer, 0));
        var conditionAvg = conditionScore.div(this.childrenCount());
        return Number(toFixed(conditionAvg, 1));
      case ItemType.condition:
        var termScore = this.score[`term${term}`];
        var time1 = termScore ? termScore.time1 : 0;
        var time2 = termScore ? termScore.time2 : 0;
        return (time1 + time2) / 2;
    }
  }

  allComputedScore() {
    let term11 = this.computedScore(1, 1);
    let term12 = this.computedScore(1, 2);
    let term21 = this.computedScore(2, 1);
    let term22 = this.computedScore(2, 2);

    let term1 = term12 > 0 ? term12 : term11;
    let term2 = term22 > 0 ? term22 : term21;

    return term2 > 0 ? term2 : term1;
  }

  childrenScore(term) {
    let time1 = this.computedScore(term, 1);
    let time2 = this.computedScore(term, 2);

    return time2 > 0 ? time2 : time1;
  }

  scoreColor(term, time) {
    if (this.computedScore(term, time) >= 3) {
      return "success";
    } else if (this.computedScore(term, time) >= 2) {
      return "warning";
    } else {
      return "error";
    }
  }

  allScoreColor() {
    if (this.allComputedScore() >= 3) {
      return "success";
    } else if (this.allComputedScore() >= 2) {
      return "warning";
    } else {
      return "error";
    }
  }

  textStyle() {
    switch (this.type) {
      case ItemType.topic:
        return "title";
      case ItemType.attribute:
        return "subtitle-1";
      case ItemType.indicator:
        return "body-1";
      default:
        return "body-2";
    }
  }

  icon() {
    if (this.type !== ItemType.topic) {
      return null;
    }

    switch (this.order) {
      case 1:
        return "mdi-human";
      case 2:
        return "mdi-account-heart";
      case 3:
        return "mdi-human-male-child";
      case 4:
        return "mdi-brain";
      default:
        return "mdi-human";
    }
  }

  typePrefix() {
    switch (this.type) {
      case "topic":
        return "";
      case "attribute":
        return this.studyClass === "ปฐมวัย (อายุต่ำกว่า 3 ปี)"
          ? `คุณลักษณะที่พึงประสงค์ที่ ${this.order}`
          : `มาตรฐานที่ ${this.order}`;
      case "indicator":
        return this.studyClass === "ปฐมวัย (อายุต่ำกว่า 3 ปี)"
          ? `คุณลักษณะที่ ${this.order}`
          : `ตัวบ่งชี้ที่ ${this.order}`;
      case "condition":
        return `${this.parent.order}.${this.order}`;
      default:
        return "";
    }
  }

  isWeightCheck() {
    return this.type === "condition" && this.name.includes("มีน้ำหนัก");
  }

  isHeightCheck() {
    return this.type === "condition" && this.name.includes("มีส่วนสูง");
  }

  printLines() {
    let childrenCount = this.childrenCount();
    let childOfChildCount = 0;
    this.children.forEach(attr => {
      childOfChildCount += attr.childrenCount();
    });

    return childOfChildCount + childrenCount;
  }
}

class TopicCalculator {
  constructor(topics) {
    this.topics = topics;
  }

  getAttributeSummary() {
    this.topics.forEach(topic => {
      topic.children.forEach(attr => {
        attr.developmentTopic = topic;
      });
    });

    let topics = this.topics.map(topic => topic.children);
    let attributes = topics.flat();
    let results = attributes.map(attribute => {
      return {
        id: attribute.id,
        order: attribute.order,
        title: attribute.title,
        topic: {
          title: attribute.developmentTopic.title,
          score: attribute.developmentTopic.allComputedScore()
        }
      };
    });

    return results;
  }

  totalScore(term, time) {
    if (this.topics.length <= 0) {
      return 0;
    }

    const reducer = (accumulator, formItem) =>
      accumulator + formItem.computedScore(term, time);

    let total = this.topics.reduce(reducer, 0);
    let score = new Big(total);
    return score.div(this.topics.length).toFixed(2);
  }
}

export { EvaluateFormItem, ItemType, TopicCalculator };
